import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, XCircle, Info, CircleNotch, ShieldCheck } from "@phosphor-icons/react";
import { complianceHealthCheck, listShopifyConnections } from "../lib/api";

const L = {
  fr: { title: "Rapport de conformité de la boutique", sub: "Vérifie les pages légales, les coordonnées et les paramètres exigés par Google Merchant Center.", run: "Vérifier la conformité de ma boutique", running: "Analyse...", lang: "Langue du rapport", none: "Aucune connexion Shopify active — connectez votre boutique dans l'onglet Shopify.", score: "Note", ok: "OK", warn: "Avertissements", fail: "Bloquants", err: "Erreur lors de l'analyse" },
  en: { title: "Store compliance report", sub: "Checks the legal pages, contact details and settings required by Google Merchant Center.", run: "Check my store compliance", running: "Analyzing...", lang: "Report language", none: "No active Shopify connection — connect your store in the Shopify tab.", score: "Score", ok: "OK", warn: "Warnings", fail: "Blocking", err: "Error during analysis" },
  es: { title: "Informe de conformidad de la tienda", sub: "Verifica las páginas legales, los datos de contacto y los ajustes exigidos por Google Merchant Center.", run: "Verificar la conformidad de mi tienda", running: "Analizando...", lang: "Idioma del informe", none: "Ninguna conexión Shopify activa — conecte su tienda en la pestaña Shopify.", score: "Nota", ok: "OK", warn: "Avisos", fail: "Bloqueantes", err: "Error durante el análisis" },
  it: { title: "Report di conformità del negozio", sub: "Verifica le pagine legali, i contatti e le impostazioni richieste da Google Merchant Center.", run: "Verifica la conformità del mio negozio", running: "Analisi...", lang: "Lingua del report", none: "Nessuna connessione Shopify attiva — collega il negozio nella scheda Shopify.", score: "Voto", ok: "OK", warn: "Avvisi", fail: "Bloccanti", err: "Errore durante l'analisi" },
  pt: { title: "Relatório de conformidade da loja", sub: "Verifica as páginas legais, os contactos e as definições exigidas pelo Google Merchant Center.", run: "Verificar a conformidade da minha loja", running: "A analisar...", lang: "Idioma do relatório", none: "Nenhuma ligação Shopify ativa — ligue a sua loja no separador Shopify.", score: "Nota", ok: "OK", warn: "Avisos", fail: "Bloqueantes", err: "Erro durante a análise" },
  de: { title: "Konformitätsbericht des Shops", sub: "Prüft Rechtsseiten, Kontaktdaten und Einstellungen, die Google Merchant Center verlangt.", run: "Konformität meines Shops prüfen", running: "Analyse...", lang: "Berichtssprache", none: "Keine aktive Shopify-Verbindung — verbinden Sie Ihren Shop im Tab Shopify.", score: "Note", ok: "OK", warn: "Warnungen", fail: "Blockierend", err: "Fehler bei der Analyse" },
};
const LANGS = [["fr", "🇫🇷"], ["en", "🇬🇧"], ["es", "🇪🇸"], ["it", "🇮🇹"], ["pt", "🇵🇹"], ["de", "🇩🇪"]];
const STATUS = {
  ok: { Icon: CheckCircle, cls: "text-emerald-700" },
  warn: { Icon: WarningCircle, cls: "text-amber-600" },
  fail: { Icon: XCircle, cls: "text-rose-700" },
  info: { Icon: Info, cls: "text-sky-700" },
};
const gradeCls = (g) =>
  g === "A" ? "bg-emerald-700" : g === "B" ? "bg-lime-700" : g === "C" ? "bg-amber-600" : g === "D" ? "bg-orange-600" : "bg-rose-700";

function CheckRow({ c }) {
  const { Icon, cls } = STATUS[c.status] || STATUS.info;
  return (
    <li className="flex items-start gap-2 py-1.5 border-b border-neutral-100 last:border-0" data-testid={`health-check-${c.key}`}>
      <Icon size={16} weight="fill" className={`${cls} flex-shrink-0 mt-0.5`} />
      <div className="min-w-0">
        <div className="text-xs font-mono text-neutral-900">{c.label}</div>
        {c.detail && <div className="text-[11px] font-mono text-neutral-500 break-words">{c.detail}</div>}
      </div>
    </li>
  );
}

export const ComplianceHealthPanel = ({ lang, wizardLang }) => {
  const [reportLang, setReportLang] = useState(lang || "fr");
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const s = L[reportLang] || L.fr;

  useEffect(() => {
    setReportLang(lang || "fr");
    if (report) run(lang || "fr");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);
  useEffect(() => { listShopifyConnections().then(setConns).catch(() => setConns([])); }, []);

  const run = async (code = reportLang) => {
    if (!conns.length) return toast.error((L[code] || L.fr).none);
    setLoading(true);
    try {
      setReport(await complianceHealthCheck({ connection_id: conns[0].id, language: code }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || (L[code] || L.fr).err);
    } finally {
      setLoading(false);
    }
  };

  const pickLang = (code) => {
    setReportLang(code);
    if (report) run(code);
  };

  return (
    <div className="border border-neutral-900 bg-white" data-testid="compliance-health-panel">
      <div className="flex flex-wrap items-start gap-3 p-4 border-b border-neutral-200">
        <ShieldCheck size={20} weight="fill" className="text-neutral-900 mt-0.5" />
        <div className="flex-1 min-w-[220px]">
          <h2 className="text-sm uppercase tracking-[0.18em] font-mono font-bold text-neutral-900">{s.title}</h2>
          <p className="text-xs font-mono text-neutral-600 mt-1">{conns.length ? `${s.sub} — ${conns[0].store_url}` : s.none}</p>
        </div>
        <button type="button" onClick={() => run()} disabled={loading || !conns.length}
          className="inline-flex items-center gap-2 bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50 px-3 py-2 text-[11px] uppercase tracking-widest font-medium ma-trans"
          data-testid="health-run-btn">
          {loading ? <CircleNotch size={12} className="animate-spin" /> : <ShieldCheck size={12} weight="bold" />}
          {loading ? s.running : s.run}
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 bg-neutral-50 border-b border-neutral-200">
        <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-neutral-600 mr-1">{s.lang}</span>
        {LANGS.map(([code, flag]) => (
          <button key={code} type="button" onClick={() => pickLang(code)}
            className={`px-2 py-1 text-[10px] font-mono uppercase border ma-trans ${reportLang === code ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 hover:border-neutral-900"}`}
            data-testid={`health-lang-${code}`}>
            {flag} {code}
          </button>
        ))}
        {wizardLang && wizardLang !== reportLang && (
          <button type="button" onClick={() => pickLang(wizardLang)} className="ml-auto text-[10px] font-mono underline text-violet-700" data-testid="health-use-wizard-lang">
            = Wizard ({wizardLang.toUpperCase()})
          </button>
        )}
      </div>
      {report && (
        <div className="p-4 grid grid-cols-1 md:grid-cols-[160px_1fr] gap-4" data-testid="health-report">
          <div className="flex md:flex-col items-center md:items-start gap-3">
            <div className={`w-16 h-16 flex items-center justify-center text-white text-3xl font-bold ${gradeCls(report.grade)}`} data-testid="health-grade">{report.grade}</div>
            <div className="text-xs font-mono space-y-0.5">
              <div data-testid="health-score">{s.score} : <strong>{report.score}/100</strong></div>
              <div className="text-emerald-700">{s.ok} : {report.total_ok}</div>
              <div className="text-amber-600">{s.warn} : {report.total_warn}</div>
              <div className="text-rose-700">{s.fail} : {report.total_fail}</div>
            </div>
          </div>
          <ul className="max-h-[360px] overflow-auto">{report.checks.map((c) => <CheckRow key={c.key} c={c} />)}</ul>
        </div>
      )}
    </div>
  );
};
